import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Modal,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ReportService from "../../../services/reportService";
import mapicon from "../../../assets/Dashboard/mapicon.svg";
import empty from "../../../assets/empty.png";
import StatusDropdown from "../../../components/StatusDropdown";
// Colors for status chart
const STATUS_COLORS = {
  completed: "#66BB6A",
  in_progress: "#42A5F5",
  pending: "#FFA726",
  assigned: "#FFA726",
  delayed: "#EF5350",
  overdue: "#EF5350",
  cancelled: "#9E9E9E",
  not_done: "#EF5350",
};

// Colors for priority chart
const PRIORITY_COLORS = {
  critical: "#D32F2F",
  urgent: "#EF5350",
  high: "#FF7043",
  medium: "#42A5F5",
  low: "#66BB6A",
  normal: "#9E9E9E",
};

const DEFAULT_COLORS = ["#66BB6A", "#42A5F5", "#FFA726", "#EF5350", "#AB47BC", "#9E9E9E"];

const WorkerTaskReport = ({ dateRange }) => {
  const { user } = useSelector((state) => state.auth);
  const companyId = user?.company_id;
  const workerId = user?.id;
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [chartData, setChartData] = useState({ 
    statusBar: [], 
    statusPie: [], 
    priorityPie: [],
    total: 0,
    completed: 0,
    completionRate: 0,
    overdue: 0
  });
  const [summary, setSummary] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);

  const fetchReport = async () => {
    if (!dateRange.startDate || !dateRange.endDate || !companyId || !workerId) return;

    setLoading(true);
    try {
      const response = await ReportService.getTaskReport(
        dateRange.startDate,
        dateRange.endDate,
        workerId,
        companyId
      );
      console.log("Task Report Response:", response);

      if (!response.error && response.data?.data) {
        const data = response.data.data;
        const taskSummary = response.data.task_summary;
        
        setReportData(data);
        setSummary({
          records_count: response.data.records_count,
          generated_at: response.data.generated_at,
          task_summary: taskSummary,
        });

        // Process chart data from API response
        processChartData(data, taskSummary, response.data.chart_data);
      } else {
        setReportData([]);
        setChartData({ statusBar: [], statusPie: [], priorityPie: [], total: 0, completed: 0, completionRate: 0, overdue: 0 });
        setSummary(null);
      }
    } catch (e) {
      console.error("Error fetching task report:", e);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (data, taskSummary, apiChartData) => {
    let statusPieData = [];
    let priorityPieData = [];
    let statusBarData = [];
    let total = data.length;
    let completed = 0;
    let completionRate = 0;
    let overdue = 0;

    // Use task_summary from API if available
    if (taskSummary) {
      total = taskSummary.total_tasks || data.length;
      overdue = taskSummary.overdue_tasks || 0;
      completionRate = taskSummary.completion_rate || 0;

      // Use status_distribution from API
      if (taskSummary.status_distribution) {
        const statusDist = taskSummary.status_distribution;
        completed = statusDist.completed || 0;

        statusPieData = Object.entries(statusDist)
          .filter(([_, count]) => count > 0)
          .map(([status, count]) => ({
            status,
            value: count,
            color: STATUS_COLORS[status.toLowerCase()] || DEFAULT_COLORS[0],
          }));

        statusBarData = Object.entries(statusDist)
          .filter(([_, count]) => count > 0)
          .map(([status, count]) => ({
            status,
            tasks: count,
          }));
      }

      // Use priority_distribution from API
      if (taskSummary.priority_distribution) {
        const priorityDist = taskSummary.priority_distribution;

        priorityPieData = Object.entries(priorityDist)
          .filter(([_, count]) => count > 0)
          .map(([priority, count]) => ({
            priority,
            value: count,
            color: PRIORITY_COLORS[priority.toLowerCase()] || DEFAULT_COLORS[0],
          }));
      }
    }

    // Fallback: Calculate from individual task records if API doesn't provide summary
    if (statusPieData.length === 0 && data.length > 0) {
      const statusCount = {};
      const priorityCount = {};

      data.forEach((task) => {
        // Status counts
        const status = task.status || "unknown";
        statusCount[status] = (statusCount[status] || 0) + 1;

        // Priority counts
        const priority = task.priority || "normal";
        priorityCount[priority] = (priorityCount[priority] || 0) + 1;
      });

      completed = statusCount["completed"] || statusCount["Completed"] || 0;
      completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

      statusPieData = Object.entries(statusCount)
        .filter(([_, count]) => count > 0)
        .map(([status, count]) => ({
          status,
          value: count,
          color: STATUS_COLORS[status.toLowerCase()] || DEFAULT_COLORS[0],
        }));

      statusBarData = Object.entries(statusCount)
        .filter(([_, count]) => count > 0)
        .map(([status, count]) => ({
          status,
          tasks: count,
        }));

      priorityPieData = Object.entries(priorityCount)
        .filter(([_, count]) => count > 0)
        .map(([priority, count]) => ({
          priority,
          value: count,
          color: PRIORITY_COLORS[priority.toLowerCase()] || DEFAULT_COLORS[0],
        }));
    }

    setChartData({
      statusBar: statusBarData,
      statusPie: statusPieData,
      priorityPie: priorityPieData,
      total,
      completed,
      completionRate,
      overdue,
    });
  };

  const formatStatusName = (status) => {
    if (!status) return "Unknown";
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatPriorityName = (priority) => {
    if (!priority) return "Normal";
    return priority
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Pie slice label styled to match the card header font (Poppins) instead of Recharts' default
  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 22;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        style={{ fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: 500, fill: "#44546F" }}
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  useEffect(() => {
    fetchReport();
  }, [dateRange.startDate, dateRange.endDate, companyId, workerId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "completed":
        return { bg: "#E8F5E9", color: "#2E7D32" };
      case "in_progress":
      case "in progress":
      case "start":
        return { bg: "#E3F2FD", color: "#1565C0" };
      case "pending":
      case "assigned":
        return { bg: "#FFF3E0", color: "#E65100" };
      case "overdue":
      case "delayed":
      case "not_done":
        return { bg: "#FFEBEE", color: "#C62828" };
      case "cancelled":
        return { bg: "#F5F5F5", color: "#757575" };
      default:
        return { bg: "#F5F5F5", color: "#757575" };
    }
  };

  const getPriorityColor = (priority) => {
    const priorityLower = priority?.toLowerCase();
    switch (priorityLower) {
      case "critical":
        return { bg: "#FFCDD2", color: "#C62828" };
      case "urgent":
        return { bg: "#FFEBEE", color: "#C62828" };
      case "high":
        return { bg: "#FFF3E0", color: "#E65100" };
      case "medium":
        return { bg: "#E3F2FD", color: "#1565C0" };
      case "low":
        return { bg: "#E8F5E9", color: "#2E7D32" };
      default:
        return { bg: "#F5F5F5", color: "#757575" };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            color: "#003149",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          {t("reportsScreen.employeeTaskComplianceReport")}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "#6B7280",
            fontFamily: "Poppins, sans-serif",
            mt: 0.5,
          }}
        >
         {t("reportsScreen.taskApiDesc")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {/* Bar Chart - Tasks by Status */}
        <Card
          sx={{
            flex: "1 1 48%",
            minWidth: 300,
            p: 2,
            border: "2px solid #091E4224",
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#44546F",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {t("reportsScreen.taskDistributionByStatus")}
              </Typography>
              
            </Box>
            <IconButton onClick={() => setOpenDetailModal(true)}>
              <img src={mapicon} alt="details" style={{ width: 33, height: 33 }} />
            </IconButton>
          </Box>

          {chartData.statusBar.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.statusBar} barCategoryGap="30%">
                <XAxis dataKey="status" tickFormatter={formatStatusName} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={formatStatusName} />
                <Legend />
                <Bar
                  dataKey="tasks"
                  name={t("reportsScreen.tasks")}
                  fill="#42A5F5"
                  barSize={45}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box height={300} display="flex" alignItems="center" justifyContent="center">
              <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
            </Box>
          )}
        </Card>

        {/* Pie Chart - Status Distribution */}
        <Card
          sx={{
            flex: "1 1 48%",
            minWidth: 300,
            p: 2,
            border: "2px solid #091E4224",
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#44546F",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {t("reportsScreen.taskStatusAnalysis")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#9CA3AF",
                  fontFamily: "Poppins, sans-serif",
                  mt: 0.3,
                }}
              >
              {t("reportsScreen.completed")}, {t("reportsScreen.overdue")}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenDetailModal(true)}>
              <img src={mapicon} alt="details" style={{ width: 33, height: 33 }} />
            </IconButton>
          </Box>

          {chartData.statusPie.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.statusPie.map((d) => ({ ...d, name: formatStatusName(d.status) }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={4}
                  dataKey="value"
                  label={renderPieLabel}
                >
                  {chartData.statusPie.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ fontFamily: "Poppins, sans-serif", fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box height={300} display="flex" alignItems="center" justifyContent="center">
              <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
            </Box>
          )}
        </Card>

        {/* Pie Chart - Priority Distribution */}
        <Card
          sx={{
            flex: "1 1 48%",
            minWidth: 300,
            p: 2,
            border: "2px solid #091E4224",
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#44546F",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {t("reportsScreen.taskDistributionByPriority")}
              </Typography>
           
            </Box>
            <IconButton onClick={() => setOpenDetailModal(true)}>
              <img src={mapicon} alt="details" style={{ width: 33, height: 33 }} />
            </IconButton>
          </Box>

          {chartData.priorityPie.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.priorityPie.map((d) => ({ ...d, name: formatPriorityName(d.priority) }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={4}
                  dataKey="value"
                  label={renderPieLabel}
                >
                  {chartData.priorityPie.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ fontFamily: "Poppins, sans-serif", fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box height={300} display="flex" alignItems="center" justifyContent="center">
              <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
            </Box>
          )}
        </Card>

        {/* Summary Stats Card */}
        <Card
          sx={{
            flex: "1 1 48%",
            minWidth: 300,
            p: 3,
            border: "2px solid #091E4224",
            borderRadius: 2,
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: "15px",
                color: "#44546F",
                fontWeight: 500,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("reportsScreen.taskCompletionMetricsTitle")}
            </Typography>
    
          </Box>

          <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}>
              <Box sx={{ textAlign: "center", flex: 1 }}>
                <Typography
                  sx={{ fontSize: "36px", fontWeight: 700, color: "#42A5F5", fontFamily: "Poppins" }}
                >
                  {chartData.total || 0}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#666", fontFamily: "Poppins" }}>
                  {t("reportsScreen.totalTasks")}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center", flex: 1 }}>
                <Typography
                  sx={{ fontSize: "36px", fontWeight: 700, color: "#66BB6A", fontFamily: "Poppins" }}
                >
                  {chartData.completed || 0}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#666", fontFamily: "Poppins" }}>
                  {t("reportsScreen.completed")}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center", flex: 1 }}>
                <Typography
                  sx={{ fontSize: "36px", fontWeight: 700, color: "#FFA726", fontFamily: "Poppins" }}
                >
                  {chartData.completionRate || 0}%
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#666", fontFamily: "Poppins" }}>
                  {t("reportsScreen.completionRate")}
                </Typography>
              </Box>
              {chartData.overdue > 0 && (
                <Box sx={{ textAlign: "center", flex: 1 }}>
                  <Typography
                    sx={{ fontSize: "36px", fontWeight: 700, color: "#EF5350", fontFamily: "Poppins" }}
                  >
                    {chartData.overdue}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#666", fontFamily: "Poppins" }}>
                    {t("reportsScreen.overdue")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Detail Modal with Table */}
      <Modal open={openDetailModal} onClose={() => setOpenDetailModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            width: "90%",
            maxWidth: 1000,
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#003149", fontFamily: "Poppins, sans-serif" }}
            >
              {t("reportsScreen.taskDetails")}
            </Typography>
            <IconButton onClick={() => setOpenDetailModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {summary && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "#F5F5F5", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: "Poppins, sans-serif" }}>
                <strong>{t("reportsScreen.totalTasks")}:</strong> {chartData.total} |{" "}
                <strong>{t("reportsScreen.completed")}:</strong> {chartData.completed} |{" "}
                <strong>{t("reportsScreen.completionRate")}:</strong> {chartData.completionRate}%
              </Typography>
            </Box>
          )}

          {reportData.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                       {t("attendanceLabels.taskTable.taskName")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                     {t("attendanceLabels.taskTable.assignedBy")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                         {t("attendanceLabels.taskTable.priority")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                    {t("attendanceLabels.taskTable.deadline")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                    {t("attendanceLabels.taskTable.status")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((task, index) => {
                    const statusColors = getStatusColor(task.status);
                    const priorityColors = getPriorityColor(task.priority);
                    return (
                      <TableRow key={task.id || index} hover>
                        <TableCell sx={{ fontFamily: "Poppins, sans-serif", maxWidth: 200 }}>
                          <Typography noWrap sx={{ fontSize: "13px" }} title={task.title}>
                            {task.title || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px" }}>
                          {task.assigned_by || "-"}
                        </TableCell>
                        <TableCell>
                          {task.priority ? (
                           
                              <Box sx={{ pointerEvents: "none" }}>
                                        <StatusDropdown currentStatus={task.priority} />
                                      </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px" }}>
                          {formatDate(task.deadline)}
                        </TableCell>
                        <TableCell>
                         <Box sx={{ pointerEvents: "none" }}>
                                   <StatusDropdown currentStatus={task.status} />
                                 </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ textAlign: "center", py: 4 }}>{t("reportsScreen.noData")}</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default WorkerTaskReport;
